import { memo, useState } from 'react';
import { useRouter } from 'next/router';

import { Checkbox, FieldSet, Label, LabeledControl, Legend, Toast, useToast } from '@zillow/constellation-10';

import { DatarouterError } from '@zg-rentals/rental-manager-env';
import {
  useRentalManagerUserPropertyEditWebApiHandler_hasBusinessCenterCommunityRoom,
  useRentalManagerUserPropertyEditWebApiHandler_hasClubHouseCommunityRoom,
  useRentalManagerUserPropertyEditWebApiHandler_hasConferenceRoomCommunityRoom,
  useRentalManagerUserPropertyEditWebApiHandler_hasFitnessCenterCommunityRoom,
  useRentalManagerUserPropertyEditWebApiHandler_hasGameRoomCommunityRoom,
  useRentalManagerUserPropertyEditWebApiHandler_hasLibraryCommunityRoom,
  useRentalManagerUserPropertyEditWebApiHandler_hasLoungeCommunityRoom,
  useRentalManagerUserPropertyEditWebApiHandler_hasPartyRoomCommunityRoom,
  useRentalManagerUserPropertyEditWebApiHandler_hasPetWashingStationCommunityRoom,
  useRentalManagerUserPropertyEditWebApiHandler_hasTheaterCommunityRoom,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { useBatchMutationToast } from '../../../../hooks/useBatchMutationToast';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';
import { getIsInputFormDisabled } from '../../utils';

import {
  COMMUNITY_ROOM_KEYS,
  COMMUNITY_ROOM_METADATA,
  type CommunityRoomKey,
} from '../../../../constants/KeyFeaturesConstants';

import { useAuth } from '../../../AuthProvider';

type LocalState = Record<CommunityRoomKey, boolean>;

const CommunityRoomsAmenities = (): JSX.Element | null => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const communityRoomTypes = listingDetails?.buildingAttributes?.communityRoomTypes;
  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const [localState, setLocalState] = useState<LocalState>({
    hasBusinessCenter: communityRoomTypes?.hasBusinessCenter ?? false,
    hasClubHouse: communityRoomTypes?.hasClubHouse ?? false,
    hasConferenceRoom: communityRoomTypes?.hasConferenceRoom ?? false,
    hasFitnessCenter: communityRoomTypes?.hasFitnessCenter ?? false,
    hasGameRoom: communityRoomTypes?.hasGameRoom ?? false,
    hasLibrary: communityRoomTypes?.hasLibrary ?? false,
    hasLounge: communityRoomTypes?.hasLounge ?? false,
    hasPartyRoom: communityRoomTypes?.hasPartyRoom ?? false,
    hasPetWashingStation: communityRoomTypes?.hasPetWashingStation ?? false,
    hasTheater: communityRoomTypes?.hasTheater ?? false,
  });

  const { startRequest, handleSuccess, handleError } = useBatchMutationToast({
    successMessage: 'Community rooms updated',
  });

  const isInputFormDisabled = getIsInputFormDisabled(listingDetails, canUpdateListing);

  const { mutateAsync: updateBusinessCenter } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasBusinessCenterCommunityRoom,
    skipToast: true,
    meta: { context: { api: 'hasBusinessCenterCommunityRoom', details: { listingAlias } } },
  });

  const { mutateAsync: updateClubHouse } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasClubHouseCommunityRoom,
    skipToast: true,
    meta: { context: { api: 'hasClubHouseCommunityRoom', details: { listingAlias } } },
  });

  const { mutateAsync: updateConferenceRoom } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasConferenceRoomCommunityRoom,
    skipToast: true,
    meta: { context: { api: 'hasConferenceRoomCommunityRoom', details: { listingAlias } } },
  });

  const { mutateAsync: updateFitnessCenter } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasFitnessCenterCommunityRoom,
    skipToast: true,
    meta: { context: { api: 'hasFitnessCenterCommunityRoom', details: { listingAlias } } },
  });

  const { mutateAsync: updateGameRoom } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasGameRoomCommunityRoom,
    skipToast: true,
    meta: { context: { api: 'hasGameRoomCommunityRoom', details: { listingAlias } } },
  });

  const { mutateAsync: updateLibrary } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasLibraryCommunityRoom,
    skipToast: true,
    meta: { context: { api: 'hasLibraryCommunityRoom', details: { listingAlias } } },
  });

  const { mutateAsync: updateLounge } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasLoungeCommunityRoom,
    skipToast: true,
    meta: { context: { api: 'hasLoungeCommunityRoom', details: { listingAlias } } },
  });

  const { mutateAsync: updatePartyRoom } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasPartyRoomCommunityRoom,
    skipToast: true,
    meta: { context: { api: 'hasPartyRoomCommunityRoom', details: { listingAlias } } },
  });

  const { mutateAsync: updatePetWashingStation } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasPetWashingStationCommunityRoom,
    skipToast: true,
    meta: { context: { api: 'hasPetWashingStationCommunityRoom', details: { listingAlias } } },
  });

  const { mutateAsync: updateTheater } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasTheaterCommunityRoom,
    skipToast: true,
    meta: { context: { api: 'hasTheaterCommunityRoom', details: { listingAlias } } },
  });

  const mutationMap: Record<keyof LocalState, typeof updateBusinessCenter> = {
    hasBusinessCenter: updateBusinessCenter,
    hasClubHouse: updateClubHouse,
    hasConferenceRoom: updateConferenceRoom,
    hasFitnessCenter: updateFitnessCenter,
    hasGameRoom: updateGameRoom,
    hasLibrary: updateLibrary,
    hasLounge: updateLounge,
    hasPartyRoom: updatePartyRoom,
    hasPetWashingStation: updatePetWashingStation,
    hasTheater: updateTheater,
  };

  const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = event.target.name as CommunityRoomKey;
    const checked = event.target.value === 'true';
    const previousValue = !checked;

    startRequest();
    setLocalState((prev) => ({ ...prev, [fieldName]: checked }));

    try {
      const metadata = COMMUNITY_ROOM_METADATA[fieldName];
      await mutationMap[fieldName]({
        getParams: {
          [metadata.apiKey]: checked,
          listingAlias,
        },
      });
      handleSuccess();
    } catch (err) {
      handleError();
      setLocalState((prev) => ({ ...prev, [fieldName]: previousValue }));
      const errorMessage = DatarouterError.getDisplayMessage(
        err,
        'Something went wrong, refresh the page and try again',
      );
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  };

  return (
    <FieldSet legend={<Legend>Community rooms</Legend>} disabled={isInputFormDisabled}>
      {COMMUNITY_ROOM_KEYS.map((fieldName) => {
        const metadata = COMMUNITY_ROOM_METADATA[fieldName];
        return (
          <LabeledControl
            key={`communityRoomTypes-${metadata.label}`}
            label={<Label>{metadata.label}</Label>}
            control={
              <Checkbox
                name={fieldName}
                value={String(!localState[fieldName])}
                checked={localState[fieldName]}
                onChange={handleCheckboxChange}
              />
            }
          />
        );
      })}
    </FieldSet>
  );
};

export default memo(CommunityRoomsAmenities);
